import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Lock, Eye, EyeOff, ArrowRight, Loader2, CheckCircle, XCircle, Sparkles } from 'lucide-react';
import { authService } from '../services/auth';

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => {
    // Get the access token from the URL hash (Supabase puts it there after recovery)
    const hashParams = new URLSearchParams(location.hash.substring(1));
    const token = hashParams.get('access_token');
    const type = hashParams.get('type');

    if (token && type === 'recovery') {
      setAccessToken(token);
    } else {
      // Check if token was passed via state (from AuthCallback)
      const stateToken = location.state?.accessToken;
      if (stateToken) {
        setAccessToken(stateToken);
      } else {
        setError('Invalid or expired reset link. Please request a new password reset.');
      }
    }
  }, [location]);

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !accessToken) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await authService.updatePassword(accessToken, password);
      setIsSuccess(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to update password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (validationErrors.password) {
      setValidationErrors((prev) => ({ ...prev, password: '' }));
    }
    if (confirmPassword && e.target.value === confirmPassword && validationErrors.confirmPassword) {
      setValidationErrors((prev) => ({ ...prev, confirmPassword: '' }));
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    if (validationErrors.confirmPassword && password === e.target.value) {
      setValidationErrors((prev) => ({ ...prev, confirmPassword: '' }));
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-surface px-4">
        <Card className="w-full max-w-md auth-card">
          <CardHeader className="text-center space-y-2 pb-6">
            <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle className="text-2xl font-bold">Password updated!</CardTitle>
            <CardDescription className="text-base text-muted-foreground">
              Your password has been successfully reset.
            </CardDescription>
          </CardHeader>

          <CardFooter className="pt-6">
            <Button
              className="w-full h-12 btn-primary text-base font-medium !text-white"
              onClick={() => navigate('/login')}
            >
              Continue to login
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (!accessToken && error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-surface px-4">
        <Card className="w-full max-w-md auth-card">
          <CardHeader className="text-center space-y-2 pb-6">
            <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
              <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-2xl font-bold">Invalid reset link</CardTitle>
            <CardDescription className="text-base text-muted-foreground">
              {error}
            </CardDescription>
          </CardHeader>

          <CardFooter className="pt-6">
            <Button
              className="w-full h-12 btn-primary text-base font-medium !text-white"
              onClick={() => navigate('/login')}
            >
              Back to login
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-surface animate-fade-in">
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="ml-3 text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                TinyScale
              </span>
            </div>
          </div>

          <Card className="auth-card">
            <CardHeader className="text-center space-y-2 pb-6">
              <CardTitle className="text-2xl font-bold">Set new password</CardTitle>
              <CardDescription className="text-base text-muted-foreground">
                Enter your new password below
              </CardDescription>
            </CardHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              <CardContent className="space-y-6">
                {error && (
                  <Alert variant="destructive" className="animate-bounce-in">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-3">
                  <Label htmlFor="password" className="text-sm font-medium text-foreground">
                    New password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password (min. 6 characters)"
                      value={password}
                      onChange={handlePasswordChange}
                      className={`pl-10 pr-10 h-12 input-enhanced ${
                        validationErrors.password
                          ? 'border-destructive focus:border-destructive ring-destructive/20'
                          : ''
                      }`}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {validationErrors.password && (
                    <p className="text-sm text-destructive animate-slide-down">
                      {validationErrors.password}
                    </p>
                  )}
                </div>

                <div className="space-y-3">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium text-foreground">
                    Confirm new password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      className={`pl-10 pr-10 h-12 input-enhanced ${
                        validationErrors.confirmPassword
                          ? 'border-destructive focus:border-destructive ring-destructive/20'
                          : ''
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {validationErrors.confirmPassword && (
                    <p className="text-sm text-destructive animate-slide-down">
                      {validationErrors.confirmPassword}
                    </p>
                  )}
                </div>
              </CardContent>

              <CardFooter className="flex flex-col space-y-6 pt-6">
                <Button
                  type="submit"
                  className="w-full h-12 btn-primary text-base font-medium group relative overflow-hidden !text-white"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating password...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      Update password
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  )}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};
